import React from 'react';
import Badge from '@amzn/awsui-components-console/badge';
import Box from '@amzn/awsui-components-console/box';
import Modal from '@amzn/awsui-components-console/modal';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import { InlineMarkupType, MessageFormatType } from 'src/generated/graphql';
import { formatDayMonthYear } from 'src/common/utils/dateFormatters';
import { NOT_AVAILABLE } from '../constants';
import { inlineMarkupLabel, messageFormatLabel } from 'src/common/formatLabels';
import { TranslateFn, useTranslation } from 'src/i18n/shim/useTranslation';

interface StringVersionViewModalProps {
  visible: boolean;
  stringId: string;
  version: number;
  /** This version's source text, already fetched by the panel. */
  sourceText: string;
  /** This version's context note, already fetched by the panel. */
  contextNote: string;
  /** This version's inline markup type; absent when the backend reported UNKNOWN. */
  inlineMarkupType?: InlineMarkupType;
  /** This version's message format type; absent when the backend reported UNKNOWN. */
  messageFormatType?: MessageFormatType;
  /** The group's CX IDs recorded at this version. */
  cxIds: string[];
  /** This version's submission date (epoch ms) for the summary line. */
  updatedOn?: number | null;
  /** This version's author (Midway alias) for the summary line. */
  updatedBy?: string;
  onDismiss: () => void;
}

function markupLabel(t: TranslateFn, value?: InlineMarkupType): string {
  return value ? inlineMarkupLabel(t, value) : NOT_AVAILABLE;
}

function formatLabel(t: TranslateFn, value?: MessageFormatType): string {
  return value ? messageFormatLabel(t, value) : NOT_AVAILABLE;
}

/** Builds "Snapshot of string at v3 · 02 Sep 2026 · by menshara@". One whole-sentence resource per
 * shape — including the missing-date and missing-author shapes — rather than middot-joined
 * fragments, mirroring the group side's `sgd-view-version-snapshot-meta`. */
function buildSummary(
  t: TranslateFn,
  locale: string,
  version: number,
  updatedOn?: number | null,
  updatedBy?: string,
): string {
  const date = updatedOn
    ? formatDayMonthYear(locale, new Date(updatedOn))
    : undefined;
  if (date && updatedBy) {
    return t('string-version-snapshot-meta', {
      version,
      date,
      user: updatedBy,
    });
  }
  if (updatedBy) {
    return t('string-version-snapshot-meta-no-date', {
      version,
      user: updatedBy,
    });
  }
  if (date) {
    return t('string-version-snapshot-meta-no-author', { version, date });
  }
  return t('string-version-snapshot-summary', { version });
}

/**
 * One version's snapshot of a single string.
 *
 * Presentational only: every value is passed in from the snapshot the panel
 * already holds, so opening this modal fetches nothing. The group page's
 * `ViewVersionModal` is not reused because it lists a whole group's strings and
 * fetches to do it.
 */
const StringVersionViewModal: React.FC<StringVersionViewModalProps> = ({
  visible,
  stringId,
  version,
  sourceText,
  contextNote,
  inlineMarkupType,
  messageFormatType,
  cxIds,
  updatedOn,
  updatedBy,
  onDismiss,
}) => {
  const { t, locale } = useTranslation();

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      size="medium"
      data-testid="string-view-modal"
      closeAriaLabel={t('string-version-snapshot-close')}
      header={t('string-version-snapshot-header', { stringId, version })}
    >
      <SpaceBetween size="m">
        <Box color="text-body-secondary" fontSize="body-s">
          {buildSummary(t, locale, version, updatedOn, updatedBy)}
        </Box>

        <SpaceBetween size="xs">
          <Box fontWeight="bold">{t('string-version-cx-ids-at')}</Box>
          {cxIds.length > 0 ? (
            <SpaceBetween direction="horizontal" size="xs">
              {cxIds.map((cxId) => (
                <Badge key={cxId} color="grey">
                  {cxId}
                </Badge>
              ))}
            </SpaceBetween>
          ) : (
            <Box color="text-body-secondary" fontSize="body-s">
              {NOT_AVAILABLE}
            </Box>
          )}
        </SpaceBetween>

        <SpaceBetween size="xs">
          <div>
            <Box variant="awsui-key-label">{t('string-field-string-id')}</Box>
            <Box variant="p">{stringId}</Box>
          </div>
          <div>
            <Box variant="awsui-key-label">{t('string-field-source-text')}</Box>
            <Box variant="p" data-testid="string-view-source-text">
              {sourceText || NOT_AVAILABLE}
            </Box>
          </div>
          <div>
            <Box variant="awsui-key-label">
              {t('string-field-context-note')}
            </Box>
            <Box variant="p" data-testid="string-view-context-note">
              {contextNote || NOT_AVAILABLE}
            </Box>
          </div>
          <div>
            <Box variant="awsui-key-label">
              {t('string-field-inline-markup')}
            </Box>
            <Box variant="p" data-testid="string-view-inline-markup">
              {markupLabel(t, inlineMarkupType)}
            </Box>
          </div>
          <div>
            <Box variant="awsui-key-label">
              {t('string-field-message-format')}
            </Box>
            <Box variant="p" data-testid="string-view-message-format">
              {formatLabel(t, messageFormatType)}
            </Box>
          </div>
        </SpaceBetween>
      </SpaceBetween>
    </Modal>
  );
};

export default StringVersionViewModal;
